import { Container } from "./container.js";
import { Injectable } from "./decorators.js";
import { INJECT_METADATA_KEY, OPTIONAL_METADATA_KEY } from "./types.js";
const defaultContainer = new Container({
    autoRegister: true
});
class AutoInject {
    static{
        this.container = defaultContainer;
    }
    static{
        this.autoResolveConstructor = true;
    }
    static{
        this.autoInjectProperties = true;
    }
    static{
        this.resolutionContext = new Set();
    }
    static setContainer(container) {
        AutoInject.container = container;
    }
    static getContainer() {
        return AutoInject.container;
    }
    static setAutoResolveConstructor(enabled) {
        AutoInject.autoResolveConstructor = enabled;
    }
    static setAutoInjectProperties(enabled) {
        AutoInject.autoInjectProperties = enabled;
    }
    static isAutoResolveConstructorEnabled() {
        return AutoInject.autoResolveConstructor;
    }
    static isAutoInjectPropertiesEnabled() {
        return AutoInject.autoInjectProperties;
    }
    constructor(container){
        this._preferredLoadStrategy = 'sync';
        this._container = AutoInject.container;
        this._autoResolveConstructor = AutoInject.autoResolveConstructor;
        this._autoInjectProperties = AutoInject.autoInjectProperties;
        const currentConstructor = this.constructor;
        if (AutoInject.resolutionContext.has(currentConstructor)) return;
        if (container) this._container = container;
        if ('async' === this._preferredLoadStrategy) this.loadDependencies();
        else this.loadDependenciesSync();
    }
    setContainer(container) {
        this._container = container;
    }
    getContainer() {
        return this._container;
    }
    setAutoResolveConstructor(enabled) {
        this._autoResolveConstructor = enabled;
    }
    setAutoInjectProperties(enabled) {
        this._autoInjectProperties = enabled;
    }
    isAutoResolveConstructorEnabled() {
        return this._autoResolveConstructor;
    }
    isAutoInjectPropertiesEnabled() {
        return this._autoInjectProperties;
    }
    async loadDependencies() {
        await this.autoResolveDependencies();
        await this.autoInjectProperties();
    }
    loadDependenciesSync() {
        this.autoResolveDependenciesSync();
        this.autoInjectPropertiesSync();
    }
    async autoResolveDependencies() {
        if (!this.isAutoResolveConstructorEnabled()) return;
        const constructor = this.constructor;
        const container = this.getContainer();
        try {
            await this.autoRegisterDependencies(constructor, container);
            await this.resolveDependenciesAsync(constructor, container);
        } catch (error) {
            console.warn(`Auto-resolve dependencies failed for ${constructor.name}:`, error);
        }
    }
    autoResolveDependenciesSync() {
        if (!this.isAutoResolveConstructorEnabled()) return;
        const constructor = this.constructor;
        const container = this.getContainer();
        try {
            this.autoRegisterDependenciesSync(constructor, container);
            this.resolveDependenciesSync(constructor, container);
        } catch (error) {
            console.warn(`Auto-resolve dependencies sync failed for ${constructor.name}:`, error);
        }
    }
    async autoInjectProperties() {
        if (!this.isAutoInjectPropertiesEnabled()) return;
        const container = this.getContainer();
        try {
            await container.injectProperties(this);
        } catch (error) {
            console.warn(`Auto property injection failed for ${this.constructor.name}:`, error);
        }
    }
    autoInjectPropertiesSync() {
        if (!this.isAutoInjectPropertiesEnabled()) return;
        const container = this.getContainer();
        try {
            container.injectPropertiesSync(this);
        } catch (error) {
            console.warn(`Auto property injection sync failed for ${this.constructor.name}:`, error);
        }
    }
    async autoRegisterDependencies(constructor, container) {
        const paramTypes = this.getParameterTypes(constructor);
        const injectedTokens = this.getInjectedTokens(constructor);
        const allTokens = new Set([
            ...paramTypes,
            ...Object.values(injectedTokens)
        ]);
        for (const token of Array.from(allTokens))if (token && 'function' == typeof token && !container.has(token)) {
            const hasInjectableDecorator = Reflect.hasMetadata?.('injectable', token);
            if (hasInjectableDecorator || container.options?.autoRegister) container.registerTransient(token, token);
        }
    }
    autoRegisterDependenciesSync(constructor, container) {
        const paramTypes = this.getParameterTypes(constructor);
        const injectedTokens = this.getInjectedTokens(constructor);
        const allTokens = new Set([
            ...paramTypes,
            ...Object.values(injectedTokens)
        ]);
        for (const token of Array.from(allTokens))if (token && 'function' == typeof token && !container.has(token)) {
            const hasInjectableDecorator = Reflect.hasMetadata?.('injectable', token);
            if (hasInjectableDecorator || container.options?.autoRegister) container.registerTransient(token, token);
        }
    }
    async resolveDependenciesAsync(constructor, container) {
        const paramTypes = this.getParameterTypes(constructor);
        const injectedTokens = this.getInjectedTokens(constructor);
        const optionalParams = this.getOptionalParameters(constructor);
        const paramNames = this.getParameterNames(constructor);
        const maxLength = Math.max(paramTypes.length, Object.keys(injectedTokens).length, Object.keys(optionalParams).length, paramNames.length);
        for(let i = 0; i < maxLength; i++){
            const paramName = paramNames[i];
            let token = injectedTokens[i] || paramTypes[i];
            const isOptional = optionalParams[i] || false;
            if (!token && paramName) {
                const potentialTokens = container.getTokens();
                for (const potentialToken of potentialTokens)if ('function' == typeof potentialToken) {
                    const className = potentialToken.name.toLowerCase();
                    const paramLower = paramName.toLowerCase();
                    if (className === paramLower || className === paramLower.replace('service', '') + 'service' || className.replace('service', '') === paramLower.replace('service', '') || className === paramLower || paramLower.includes(className.replace('service', '').toLowerCase()) || className.toLowerCase().includes(paramLower.replace('service', ''))) {
                        token = potentialToken;
                        break;
                    }
                }
            }
            if (token && paramName) try {
                const dependency = await container.resolve(token, {
                    optional: isOptional
                });
                if (void 0 !== dependency) Reflect.set(this, paramName, dependency);
            } catch (error) {
                if (!isOptional) throw error;
            }
        }
    }
    resolveDependenciesSync(constructor, container) {
        const paramTypes = this.getParameterTypes(constructor);
        const injectedTokens = this.getInjectedTokens(constructor);
        const optionalParams = this.getOptionalParameters(constructor);
        const paramNames = this.getParameterNames(constructor);
        const maxLength = Math.max(paramTypes.length, Object.keys(injectedTokens).length, Object.keys(optionalParams).length, paramNames.length);
        for(let i = 0; i < maxLength; i++){
            const paramName = paramNames[i];
            let token = injectedTokens[i] || paramTypes[i];
            const isOptional = optionalParams[i] || false;
            if (!token && paramName) {
                const potentialTokens = container.getTokens();
                for (const potentialToken of potentialTokens)if ('function' == typeof potentialToken) {
                    const className = potentialToken.name.toLowerCase();
                    const paramLower = paramName.toLowerCase();
                    if (className === paramLower || className === paramLower.replace('service', '') + 'service' || className.replace('service', '') === paramLower.replace('service', '') || className === paramLower || paramLower.includes(className.replace('service', '').toLowerCase()) || className.toLowerCase().includes(paramLower.replace('service', ''))) {
                        token = potentialToken;
                        break;
                    }
                }
            }
            if (token && paramName) try {
                const dependency = container.get(token, {
                    optional: isOptional
                });
                if (void 0 !== dependency) Reflect.set(this, paramName, dependency);
            } catch (error) {
                if (!isOptional) throw error;
            }
        }
    }
    getParameterTypes(constructor) {
        if ('undefined' != typeof Reflect && Reflect.getMetadata) return Reflect.getMetadata('design:paramtypes', constructor) || Reflect.getMetadata('paramtypes', constructor) || [];
        return [];
    }
    getInjectedTokens(constructor) {
        if ('undefined' != typeof Reflect && Reflect.getMetadata) return Reflect.getMetadata(INJECT_METADATA_KEY, constructor) || {};
        return {};
    }
    getOptionalParameters(constructor) {
        if ('undefined' != typeof Reflect && Reflect.getMetadata) return Reflect.getMetadata(OPTIONAL_METADATA_KEY, constructor) || {};
        return {};
    }
    getParameterNames(constructor) {
        if ('undefined' != typeof Reflect && Reflect.getMetadata) {
            const paramNames = Reflect.getMetadata('design:paramnames', constructor);
            if (Array.isArray(paramNames)) return paramNames;
        }
        return [];
    }
    static async create() {
        const container = AutoInject.container;
        if (!container.has(this)) {
            const hasInjectableDecorator = Reflect.hasMetadata?.('injectable', this);
            if (hasInjectableDecorator || container.options?.autoRegister) container.registerTransient(this, this);
        }
        AutoInject.resolutionContext.add(this);
        try {
            const instance = await container.resolve(this, {
                optional: false
            });
            return instance;
        } finally{
            AutoInject.resolutionContext.delete(this);
        }
    }
    static createSync() {
        const container = AutoInject.container;
        if (!container.has(this)) {
            const hasInjectableDecorator = Reflect.hasMetadata?.('injectable', this);
            if (hasInjectableDecorator || container.options?.autoRegister) container.registerTransient(this, this);
        }
        AutoInject.resolutionContext.add(this);
        try {
            return container.get(this);
        } finally{
            AutoInject.resolutionContext.delete(this);
        }
    }
    static register(singleton = false) {
        const container = AutoInject.container;
        if (singleton) container.registerSingleton(this, this);
        else container.registerTransient(this, this);
    }
}
class AutoInjector {
    constructor(container = defaultContainer){
        this.container = container;
    }
    async create(constructor) {
        const autoRegisterKey = `__autoRegister_${constructor.name}_${constructor}`;
        if (!this.container.__autoRegisterInProgress) this.container.__autoRegisterInProgress = new Set();
        if (this.container.__autoRegisterInProgress.has(autoRegisterKey)) await new Promise((resolve)=>setTimeout(resolve, 1));
        else {
            this.container.__autoRegisterInProgress.add(autoRegisterKey);
            try {
                if (!this.container.has(constructor)) {
                    const autoRegisterMeta = constructor.__autoRegister__;
                    if (autoRegisterMeta) {
                        const token = autoRegisterMeta.token || constructor;
                        if (autoRegisterMeta.singleton) this.container.registerSingleton(token, constructor);
                        else this.container.registerTransient(token, constructor);
                    } else {
                        const hasInjectableDecorator = Reflect.hasMetadata?.('injectable', constructor);
                        if (hasInjectableDecorator || this.container['options']?.autoRegister) this.container.registerTransient(constructor, constructor);
                    }
                }
            } finally{
                this.container.__autoRegisterInProgress.delete(autoRegisterKey);
            }
        }
        const result = await this.container.resolve(constructor, {
            optional: false
        });
        return result;
    }
    createSync(constructor) {
        const autoRegisterKey = `__autoRegisterSync_${constructor.name}_${constructor}`;
        if (!this.container.__autoRegisterSyncInProgress) this.container.__autoRegisterSyncInProgress = new Set();
        if (!this.container.__autoRegisterSyncInProgress.has(autoRegisterKey)) {
            this.container.__autoRegisterSyncInProgress.add(autoRegisterKey);
            try {
                if (!this.container.has(constructor)) {
                    const autoRegisterMeta = constructor.__autoRegister__;
                    if (autoRegisterMeta) {
                        const token = autoRegisterMeta.token || constructor;
                        if (autoRegisterMeta.singleton) this.container.registerSingleton(token, constructor);
                        else this.container.registerTransient(token, constructor);
                    } else {
                        const hasInjectableDecorator = Reflect.hasMetadata?.('injectable', constructor);
                        if (hasInjectableDecorator || this.container['options']?.autoRegister) this.container.registerTransient(constructor, constructor);
                    }
                }
            } finally{
                this.container.__autoRegisterSyncInProgress.delete(autoRegisterKey);
            }
        }
        const result = this.container.get(constructor);
        return result;
    }
    async createAll(constructors) {
        return Promise.all(constructors.map((ctor)=>this.create(ctor)));
    }
    autoRegister(constructor, singleton = false, token) {
        const registerToken = token || constructor;
        if (singleton) this.container.registerSingleton(registerToken, constructor);
        else this.container.registerTransient(registerToken, constructor);
    }
    autoRegisterAll(classes) {
        classes.forEach(({ constructor, singleton = false, token })=>{
            this.autoRegister(constructor, singleton, token);
        });
    }
    getContainer() {
        return this.container;
    }
}
function AutoRegister(options = {}) {
    return function(constructor) {
        Injectable(constructor);
        constructor.__autoRegister__ = {
            singleton: options.singleton || false,
            token: options.token
        };
        return constructor;
    };
}
const defaultAutoInjector = new AutoInjector();
async function autoCreate(constructor) {
    return defaultAutoInjector.create(constructor);
}
function autoCreateSync(constructor) {
    return defaultAutoInjector.createSync(constructor);
}
function autoRegister(constructor, singleton = false, token) {
    defaultAutoInjector.autoRegister(constructor, singleton, token);
}
export { AutoInject, AutoInjector, AutoRegister, autoCreate, autoCreateSync, autoRegister, defaultAutoInjector };
